import { toast } from "react-toastify";

import appFeatures from "@/appFeatures";
import { STATUS_CODE } from "@/constants/errorCode";
import { MESSAGE } from "@/constants/message";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { clearUserQueryCache, queryClient } from "@/lib/react-query";
import { logoutCleanupStore, store } from "@/store";
import { setAuth } from "@/store/slice/authSlice";
import { getSubdomain } from "@/utils/location";
import { shouldSkipPartnerCheck } from "@/utils/partner";

import { datadogRum } from "@datadog/browser-rum";
import type { AxiosError } from "axios";
import axios from "axios";

// Singleton promise pattern: stores the in-flight refresh promise so that
// concurrent 401 responses all await the same refresh call instead of
// triggering multiple /auth/token requests (which would fail if the
// refresh token is single-use/rotated).
let refreshPromise: Promise<string> | null = null;

export const PATH_WITHOUT_AUTH_HEADER = [
  /\/auth\/token$/,
  /\/auth\/verify-token$/,
  /\/auth\/forgot-password$/,
  /\/auth\/reset-password$/,
  /\/auth\/sso\/providers$/,
  /\/activate-user$/,
  /\/partners\/.+$/ // This matches /partners/ followed by anything
];

const LOGOUT_PATH = /\/auth\/logout(\?|$)/;

/** Generic API base path for the API URL, applicable for all envs */
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || "";
/** Env-specific full API URL */
const ENV_API_URL = import.meta.env.VITE_API_URL || "";
const LOCAL_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "::1",
  "[::1]"
]);

type ApiLocation = Pick<Location, "host" | "hostname" | "protocol">;

export const getApiBaseUrl = (
  location: ApiLocation | undefined = typeof window === "undefined"
    ? undefined
    : window.location
) => {
  if (
    !location?.protocol ||
    !location.host ||
    LOCAL_HOSTNAMES.has(location.hostname)
  ) {
    // Default env-specific API URL
    return ENV_API_URL;
  }

  // Build the API URL based on the current location to support partner host (e.g. partner.dev.lokasi.com)
  return `${location.protocol}//${location.host}${API_BASE_PATH}`;
};

export const baseUrl = getApiBaseUrl();

const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json"
  },
  withCredentials: true
});

/**
 * Handles involuntary session expiry when refresh or a retried request fails
 * with 401. The IdP `sso_session` is preserved so `useSsoAutoStart` can
 * re-authenticate without prompting the user.
 */
export const doLogout = async () => {
  // Reset store + wipe other tabs' per-tab state (security boundary on session expiry).
  await logoutCleanupStore();

  toast.dismiss();

  const appSubdomain = getSubdomain();

  // Clear user-specific react-query cache while keeping app bootstrap partner data
  await clearUserQueryCache(queryClient, appSubdomain, {
    shouldPreservePartnerDetail: !shouldSkipPartnerCheck(appSubdomain)
  });

  // Clear datadog user
  datadogRum.clearUser();

  // Only worth saying when nothing is about to sign the user back in. With SSO on,
  // `useSsoAutoStart` recovers the session without a prompt, so this toast would
  // contradict the app the user is still looking at. With SSO off they land on the
  // password login, where it is the only explanation they get.
  if (!appFeatures.isLokasiSsoAuthEnabled) {
    showToast({
      title: MESSAGE.SESSION_EXPIRED_TITLE,
      message: MESSAGE.SESSION_EXPIRED_DESCRIPTION,
      type: EToastType.WARNING
    });
  }
  await axiosInstance.post("/v1/auth/logout"); // Clear auth cookies
};

const isUnauthorized = (status: number) => status === STATUS_CODE.UNAUTHORIZED;

/**
 * Transport-agnostic access token refresh primitive.
 *
 * Coordinates concurrent callers via `isRefreshing` / `subscribeTokenRefresh`
 * so only one `/v1/auth/token` call fires per refresh cycle — additional
 * callers subscribe and resolve with the same token. On success, updates the
 * Redux auth slice and returns the new access token. On failure, calls
 * `doLogout` before re-throwing.
 *
 * Used by both the axios response interceptor (`doRefreshToken`) and
 * non-axios consumers (e.g. SSE streams in `useEventSource` and
 * `fetchDatasetStream`) that need a fresh token to reopen their own
 * transport.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = axiosInstance
    .post("/v1/auth/token", {
      grant_type: "refresh_token",
      refresh_token: store.getState().auth.refreshToken
    })
    .then((result) => {
      const access_token = result?.data?.access_token as string;
      if (access_token) {
        store.dispatch(setAuth({ accessToken: access_token }));
      }
      return access_token;
    })
    .catch(async (err) => {
      await doLogout();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const doRefreshToken = async (
  originalConfig: AxiosError["config"] & { _retry?: boolean }
) => {
  originalConfig._retry = true;
  const access_token = await refreshAccessToken();
  originalConfig.headers["Authorization"] = `Bearer ${access_token}`;
  return axiosInstance(originalConfig);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.accessToken;
    const appSubdomain = getSubdomain();
    const appHostname =
      typeof window === "undefined" ? "" : window.location.hostname;

    if (appHostname && !shouldSkipPartnerCheck(appSubdomain)) {
      config.headers["X-App-Subdomain"] = appHostname;
    }

    // Check if the current URL path should skip auth header
    const shouldSkipAuthHeader = PATH_WITHOUT_AUTH_HEADER.some((pathPattern) =>
      pathPattern.test(config.url ?? "")
    );

    // Only add Authorization header if token exists and URL is not in the excluded list
    if (token && !shouldSkipAuthHeader) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error as Error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalConfig = error.config;
    const currentUrl = originalConfig.url ?? "";
    const shouldSkipRefreshToken =
      LOGOUT_PATH.test(currentUrl) || // if on logout path, skip the refresh token
      PATH_WITHOUT_AUTH_HEADER.some((pathPattern) =>
        pathPattern.test(currentUrl)
      );
    originalConfig._retry = originalConfig._retry ?? false;
    // Check for 401 status and ensure we haven't retried this request already
    if (!shouldSkipRefreshToken && isUnauthorized(error?.response?.status)) {
      if (originalConfig._retry) {
        // Logout user if after success refresh token, the API is still unauthorized
        await doLogout();
      } else {
        return doRefreshToken(originalConfig);
      }
    }
    return Promise.reject(error as Error);
  }
);

export default axiosInstance;
